
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Stack,
  Button,
  Avatar,
  TextField,
  InputAdornment,
} from "@mui/material";
import { useState } from "react";
import {
  Delete,
  SendRounded,
  Close,
  ChatBubbleOutline,
} from "@mui/icons-material";
import { getQuestionComments } from "../../store/slices/QuestionReview/QuestionReviewSlice";
import apiRequest from "../../request/apiRequest/apiRequest";
import { allApis } from "../../request/config";
import { useAppDispatch, useAppSelecter } from "../../hooks/storeHooks";
import { RootState } from "../../store/store";
import CustomTextField from "../../Components/CustomTextfield";
import ConfirmationDialog from "../../Components/ConfirmationDialog";

export default function CommentsSection({ que }: any) {
  const [cmt, setComments] = useState<Record<string, string>>({});
  const dispatch = useAppDispatch();
  const [openDialog, setOpenDialog] = useState(false);
  type CommentType = { id: string | number; [key: string]: any } | null;
  const [deleteDialog, setDelete] = useState<CommentType>(null);
  const { queLoading, comments } = useAppSelecter(
    (state: RootState) => state.QuestionReview
  );
  const latestComment = comments.length > 0 ? comments[0] : null;

  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: "1px solid #e5e7eb",
      }}
    >
      {/* Latest Comment */}
      {!queLoading &&
        (latestComment ? (
          <Stack
            direction="row"
            alignItems="flex-start"
            spacing={1.5}
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              bgcolor: "grey.100",
            }}
          >
            <Avatar sx={{ bgcolor: "primary.main", fontSize: 14 }}>
              {latestComment.created_by?.[0]?.toUpperCase() ?? "U"}
            </Avatar>
            <Box flex={1}>
              <Typography variant="body2">{latestComment.comment}</Typography>
              <Typography variant="caption" color="text.secondary">
                {latestComment.created_by} •{" "}
                {new Date(latestComment.created_at).toLocaleString()}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={async () => {
                setDelete(latestComment);
              }}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2}}
          >
            No recent comments
          </Typography>
        ))}

      {/* Show More Button */}
      {!queLoading && comments.length > 1 && (
        <Button
          size="small"
          startIcon={<ChatBubbleOutline />}
          onClick={() => setOpenDialog(true)}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            mb: 2,
          }}
        >
          See all the comments ({comments.length})
         
        </Button>
      )}

      {/* Add Comment Input */}
      <Stack spacing={1}>
        <Typography variant="subtitle2" fontWeight={600}>
          Add Comment
        </Typography>
        <CustomTextField
          placeholder="Type your comment..."
          variant="outlined"
          value={cmt[que.id] ?? ""}
        //   maxRows={1}
        //   minRows={1}
        //   multiline
          onChange={(e) =>
            setComments((p: any) => ({
              ...p,
              [que.id]: e.target.value,
            }))
          }
          onKeyUpCapture={async (ev: any) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              if (cmt[que.id]) {
                const res = await apiRequest({
                  api: allApis.comments.post,
                  method: "POST",
                  payload: {
                    question: que.id,
                    comment: cmt[que.id],
                    type: "question_cluster_review",
                  },
                });
                if (res) {
                  dispatch(getQuestionComments(que.id));
                  setComments((p: any) => ({ ...p, [que.id]: "" }));
                }
              }
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  color="primary"
                  onClick={async () => {
                    if (cmt[que.id]) {
                      const res = await apiRequest({
                        api: allApis.comments.post,
                        method: "POST",
                        payload: {
                          question: que.id,
                          comment: cmt[que.id],
                          type: "question_cluster_review",
                        },
                      });
                      if (res) {
                        dispatch(getQuestionComments(que.id));
                        setComments((p: any) => ({ ...p, [que.id]: "" }));
                      }
                    }
                  }}
                >
                  <SendRounded />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            "& .MuiInputBase-root": { padding: 0 },
            "& .MuiInputBase-input": { padding: "10px 12px" },
          }}
        />
      </Stack>
      <ConfirmationDialog
        title={"Confirmation"}
        content={
          deleteDialog ? (
            <Box>
              <Stack
                direction="row"
                key={deleteDialog.id}
                alignItems="flex-start"
                spacing={1.5}
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "grey.100",
                }}
              >
                <Avatar sx={{ bgcolor: "primary.main", fontSize: 14 }}>
                  {deleteDialog.created_by?.[0]?.toUpperCase() ?? "U"}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body2">
                    {deleteDialog.comment}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {deleteDialog.created_by} •{" "}
                    {new Date(deleteDialog.created_at).toLocaleString()}
                  </Typography>
                </Box>
              </Stack>
              
              <Typography>Are you sure you want to delete this comment ?</Typography>
            </Box>
          ) : (
            ""
          )
        }
        customWidth="fit-content"
        yesButtonText="Yes, Delete"
        noButtonText="No"
        open={Boolean(deleteDialog)}
        onClose={() => {
          setDelete(null);
        }}
        onSubmit={async () => {
          if (deleteDialog && deleteDialog.id) {
            const res = await apiRequest({
              api: allApis.comments.delete(deleteDialog.id),
              method: "DELETE",
            });
            if (res) dispatch(getQuestionComments(que.id));
            
          setDelete(null);
          }
        }}
      />
      {/* All Comments Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: 1,
          }}
        >
          All Comments
          <IconButton onClick={() => setOpenDialog(false)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}
                  className="scrollBluePrimarythin" sx={{
            height:"400px",overflowY:"auto"
          }}>
            {comments.map((c: any) => (
              <Stack
                direction="row"
                key={c.id}
                alignItems="flex-start"
                spacing={1.5}
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "grey.100",
                }}
              >
                <Avatar sx={{ bgcolor: "primary.main", fontSize: 14 }}>
                  {c.created_by?.[0]?.toUpperCase() ?? "U"}
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body2">{c.comment}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {c.created_by} • {new Date(c.created_at).toLocaleString()}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={async () => {
                    setDelete(latestComment);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Stack>
            ))}
          </Stack>
          <Stack spacing={1}>
            <Typography variant="subtitle2" fontWeight={600}>
              Add Comment
            </Typography>
            <CustomTextField
              placeholder="Type your comment..."
              variant="outlined"
              value={cmt[que.id] ?? ""}
            //   maxRows={1}
            //   minRows={1}
            //   multiline
              onChange={(e) =>
                setComments((p: any) => ({
                  ...p,
                  [que.id]: e.target.value,
                }))
              }
              onKeyUpCapture={async (ev: any) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  if (cmt[que.id]) {
                    const res = await apiRequest({
                      api: allApis.comments.post,
                      method: "POST",
                      payload: {
                        question: que.id,
                        comment: cmt[que.id],
                        type: "question_cluster_review",
                      },
                    });
                    if (res) {
                      dispatch(getQuestionComments(que.id));
                      setComments((p: any) => ({ ...p, [que.id]: "" }));
                    }
                  }
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      color="primary"
                      onClick={async () => {
                        if (cmt[que.id]) {
                          const res = await apiRequest({
                            api: allApis.comments.post,
                            method: "POST",
                            payload: {
                              question: que.id,
                              comment: cmt[que.id],
                              type: "question_cluster_review",
                            },
                          });
                          if (res) {
                            dispatch(getQuestionComments(que.id));
                            setComments((p: any) => ({ ...p, [que.id]: "" }));
                          }
                        }
                      }}
                    >
                      <SendRounded />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                width: "100%",
                "& .MuiInputBase-root": { padding: 0 },
                "& .MuiInputBase-input": { padding: "10px 12px" },
              }}
            />
          </Stack>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
